import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  GitMerge,
} from "lucide-react";

const InsertIntervalVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Merge with one interval",
      intervals: [
        [1, 3],
        [6, 9],
      ],
      newInterval: [2, 5],
      expected: [
        [1, 5],
        [6, 9],
      ],
    },
    {
      id: 2,
      name: "No overlap",
      intervals: [
        [1, 2],
        [3, 5],
        [6, 7],
        [8, 10],
        [12, 16],
      ],
      newInterval: [4, 8],
      expected: [
        [1, 2],
        [3, 10],
        [12, 16],
      ],
    },
    {
      id: 3,
      name: "Insert at beginning",
      intervals: [
        [3, 5],
        [6, 9],
      ],
      newInterval: [1, 2],
      expected: [
        [1, 2],
        [3, 5],
        [6, 9],
      ],
    },
    {
      id: 4,
      name: "Insert at end",
      intervals: [
        [1, 3],
        [4, 6],
      ],
      newInterval: [7, 9],
      expected: [
        [1, 3],
        [4, 6],
        [7, 9],
      ],
    },
    {
      id: 5,
      name: "Merge all intervals",
      intervals: [
        [1, 3],
        [4, 6],
        [8, 10],
      ],
      newInterval: [2, 9],
      expected: [[1, 10]],
    },
    {
      id: 6,
      name: "Empty intervals",
      intervals: [],
      newInterval: [5, 7],
      expected: [[5, 7]],
    },
    {
      id: 7,
      name: "Complete overlap",
      intervals: [[1, 5]],
      newInterval: [2, 3],
      expected: [[1, 5]],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customIntervals, setCustomIntervals] = useState("");
  const [customNewInterval, setCustomNewInterval] = useState("");
  const [intervals, setIntervals] = useState(testCases[0].intervals);
  const [newInterval, setNewInterval] = useState(testCases[0].newInterval);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "function insert(intervals, newInterval):", indent: 0 },
    { line: "result = []", indent: 1 },
    { line: "i = 0", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Add intervals before newInterval", indent: 1 },
    {
      line: "while i < intervals.length && intervals[i][1] < newInterval[0]:",
      indent: 1,
    },
    { line: "result.append(intervals[i])", indent: 2 },
    { line: "i++", indent: 2 },
    { line: "", indent: 0 },
    { line: "// Merge overlapping intervals", indent: 1 },
    {
      line: "while i < intervals.length && intervals[i][0] <= newInterval[1]:",
      indent: 1,
    },
    {
      line: "newInterval[0] = min(newInterval[0], intervals[i][0])",
      indent: 2,
    },
    {
      line: "newInterval[1] = max(newInterval[1], intervals[i][1])",
      indent: 2,
    },
    { line: "i++", indent: 2 },
    { line: "result.append(newInterval)", indent: 1 },
    { line: "", indent: 0 },
    { line: "// Add remaining intervals", indent: 1 },
    { line: "while i < intervals.length:", indent: 1 },
    { line: "result.append(intervals[i])", indent: 2 },
    { line: "i++", indent: 2 },
    { line: "", indent: 0 },
    { line: "return result", indent: 1 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (originalIntervals, newInt) => {
    const allSteps = [];
    const result = [];
    let i = 0;
    let mergedInterval = [...newInt];
    let step = 0;

    // Initial state
    allSteps.push({
      step: 0,
      intervals: originalIntervals,
      newInterval: newInt,
      result: [],
      currentIndex: -1,
      mergedInterval: null,
      phase: "init",
      description: "Initialize empty result array",
      highlightLine: 1,
    });

    // Phase 1: Add intervals before newInterval
    while (
      i < originalIntervals.length &&
      originalIntervals[i][1] < newInt[0]
    ) {
      result.push(originalIntervals[i]);

      allSteps.push({
        step: ++step,
        intervals: originalIntervals,
        newInterval: newInt,
        result: [...result],
        currentIndex: i,
        mergedInterval: null,
        phase: "before",
        comparison: {
          type: "before",
          interval: originalIntervals[i],
          condition: `${originalIntervals[i][1]} < ${newInt[0]}`,
          result: true,
        },
        description: `Interval [${originalIntervals[i][0]},${originalIntervals[i][1]}] ends before new interval starts`,
        highlightLine: 6,
      });

      i++;
    }

    // Phase 2: Merge overlapping intervals
    let mergeStarted = false;
    while (
      i < originalIntervals.length &&
      originalIntervals[i][0] <= mergedInterval[1]
    ) {
      const oldMerged = [...mergedInterval];
      mergedInterval[0] = Math.min(mergedInterval[0], originalIntervals[i][0]);
      mergedInterval[1] = Math.max(mergedInterval[1], originalIntervals[i][1]);

      allSteps.push({
        step: ++step,
        intervals: originalIntervals,
        newInterval: newInt,
        result: [...result],
        currentIndex: i,
        mergedInterval: [...mergedInterval],
        phase: "merge",
        comparison: {
          type: "overlap",
          interval: originalIntervals[i],
          condition: `${originalIntervals[i][0]} <= ${oldMerged[1]}`,
          result: true,
        },
        mergeDetails: {
          before: oldMerged,
          after: mergedInterval,
          merged: originalIntervals[i],
        },
        description: `Merge [${originalIntervals[i][0]},${originalIntervals[i][1]}] with [${oldMerged[0]},${oldMerged[1]}] → [${mergedInterval[0]},${mergedInterval[1]}]`,
        highlightLine: 11,
      });

      mergeStarted = true;
      i++;
    }

    // Add the merged interval
    result.push(mergedInterval);
    allSteps.push({
      step: ++step,
      intervals: originalIntervals,
      newInterval: newInt,
      result: [...result],
      currentIndex: i - 1,
      mergedInterval: mergedInterval,
      phase: "add-merged",
      description: `Add merged interval [${mergedInterval[0]},${mergedInterval[1]}] to result`,
      highlightLine: 14,
    });

    // Phase 3: Add remaining intervals
    while (i < originalIntervals.length) {
      result.push(originalIntervals[i]);

      allSteps.push({
        step: ++step,
        intervals: originalIntervals,
        newInterval: newInt,
        result: [...result],
        currentIndex: i,
        mergedInterval: mergedInterval,
        phase: "after",
        description: `Add remaining interval [${originalIntervals[i][0]},${originalIntervals[i][1]}]`,
        highlightLine: 18,
      });

      i++;
    }

    // Final state
    allSteps.push({
      step: ++step,
      intervals: originalIntervals,
      newInterval: newInt,
      result: [...result],
      currentIndex: -1,
      mergedInterval: null,
      phase: "complete",
      description: `Complete! Result: ${result
        .map((int) => `[${int[0]},${int[1]}]`)
        .join(", ")}`,
      highlightLine: 21,
      isFinal: true,
    });

    return allSteps;
  };

  // Initialize steps when test case changes
  useEffect(() => {
    const newSteps = calculateSteps(intervals, newInterval);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [intervals, newInterval]);

  // Auto-play functionality
  useEffect(() => {
    if (!isPlaying || finished) return;

    const timer = setTimeout(() => {
      handleNextStep();
    }, speed);

    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      const step = steps[nextStep];
      setHighlightedLine(step.highlightLine);

      if (step.isFinal) {
        setFinished(true);
        setIsPlaying(false);
      }
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      const step = steps[prevStep];
      setHighlightedLine(step.highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setIntervals(testCases[index].intervals);
    setNewInterval(testCases[index].newInterval);
  };

  const parseInterval = (str) => {
    const match = str.match(/^\[?\s*(\d+)\s*,\s*(\d+)\s*\]?$/);
    if (match) {
      return [parseInt(match[1]), parseInt(match[2])];
    }
    return null;
  };

  const parseIntervals = (str) => {
    if (!str.trim()) return [];

    const intervalMatches = str.match(/\[?\s*\d+\s*,\s*\d+\s*\]?/g);
    if (!intervalMatches) return null;

    const parsed = [];
    for (const match of intervalMatches) {
      const interval = parseInterval(match);
      if (!interval || interval[0] > interval[1]) return null;
      parsed.push(interval);
    }

    // Check if sorted
    for (let i = 1; i < parsed.length; i++) {
      if (parsed[i][0] < parsed[i - 1][0]) return null;
    }

    return parsed;
  };

  const addCustomTestCase = () => {
    try {
      const parsedIntervals = parseIntervals(customIntervals);
      const parsedNewInterval = parseInterval(customNewInterval);

      if (parsedIntervals === null) {
        alert(
          "Invalid intervals format. Use: [[1,3],[6,9]] and ensure they are sorted"
        );
        return;
      }

      if (!parsedNewInterval || parsedNewInterval[0] > parsedNewInterval[1]) {
        alert("Invalid new interval format. Use: [2,5]");
        return;
      }

      // Calculate expected result
      const expected = [];
      let i = 0;
      const merged = [...parsedNewInterval];

      // Add before
      while (i < parsedIntervals.length && parsedIntervals[i][1] < merged[0]) {
        expected.push(parsedIntervals[i]);
        i++;
      }

      // Merge
      while (i < parsedIntervals.length && parsedIntervals[i][0] <= merged[1]) {
        merged[0] = Math.min(merged[0], parsedIntervals[i][0]);
        merged[1] = Math.max(merged[1], parsedIntervals[i][1]);
        i++;
      }
      expected.push(merged);

      // Add after
      while (i < parsedIntervals.length) {
        expected.push(parsedIntervals[i]);
        i++;
      }

      const newTestCase = {
        id: Date.now(),
        name: `Custom ${testCases.length - 6}`,
        intervals: parsedIntervals,
        newInterval: parsedNewInterval,
        expected: expected,
      };

      setTestCases([...testCases, newTestCase]);
      setCustomIntervals("");
      setCustomNewInterval("");
    } catch (error) {
      alert("Invalid input format");
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      const newTestCases = testCases.filter((tc) => tc.id !== id);
      setTestCases(newTestCases);
      if (selectedTestCase >= newTestCases.length) {
        handleTestCaseChange(0);
      }
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 500) return "Fast";
    if (speed <= 1000) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  // Generate visual timeline
  const generateTimeline = () => {
    if (!currentStepData) return null;

    const allIntervals = [...currentStepData.intervals];
    if (currentStepData.mergedInterval) {
      allIntervals.push(currentStepData.mergedInterval);
    } else {
      allIntervals.push(currentStepData.newInterval);
    }

    const maxEnd = Math.max(...allIntervals.map((int) => int[1]));
    const scale = 100 / (maxEnd + 2);

    return { allIntervals, maxEnd, scale };
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <GitMerge className="text-blue-500" size={24} />
        Insert Interval
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left side - Visualization */}
        <div className="space-y-3">
          {/* Test Cases */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      Intervals:{" "}
                      {tc.intervals
                        .map((int) => `[${int[0]},${int[1]}]`)
                        .join(", ") || "[]"}
                    </div>
                    <div className="text-xs opacity-75">
                      New: [{tc.newInterval[0]},{tc.newInterval[1]}] →{" "}
                      {tc.expected
                        .map((int) => `[${int[0]},${int[1]}]`)
                        .join(", ")}
                    </div>
                  </button>
                  {tc.id > 7 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add custom test case */}
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customIntervals}
                onChange={(e) => setCustomIntervals(e.target.value)}
                placeholder="Intervals: [[1,3],[6,9]]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <input
                type="text"
                value={customNewInterval}
                onChange={(e) => setCustomNewInterval(e.target.value)}
                placeholder="New interval: [2,5]"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} />
                Add Test Case
              </button>
            </div>
          </div>

          {/* Interval Visualization */}
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            {/* Comparison Display */}
            {currentStepData?.comparison && (
              <div className="mb-3 p-2 bg-yellow-50 rounded">
                <p className="text-xs text-yellow-800 font-medium">
                  Checking: {currentStepData.comparison.condition} ={" "}
                  {currentStepData.comparison.result ? "true" : "false"}
                </p>
              </div>
            )}

            {/* Timeline Visualization */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Timeline View:
              </p>
              <div className="relative h-32 bg-gray-50 rounded p-2">
                {generateTimeline() && (
                  <>
                    {/* Grid lines */}
                    {[...Array(Math.floor(generateTimeline().maxEnd + 2))].map(
                      (_, i) => (
                        <div
                          key={i}
                          className="absolute top-0 bottom-0 border-l border-gray-300"
                          style={{ left: `${i * generateTimeline().scale}%` }}
                        >
                          <span className="absolute -bottom-5 -left-2 text-xs text-gray-500">
                            {i}
                          </span>
                        </div>
                      )
                    )}

                    {/* Original intervals */}
                    {currentStepData.intervals.map((interval, idx) => (
                      <div
                        key={`orig-${idx}`}
                        className={`absolute h-8 rounded transition-all duration-300 ${
                          idx === currentStepData.currentIndex
                            ? "bg-blue-400 border-2 border-blue-600"
                            : "bg-gray-300 border border-gray-400"
                        }`}
                        style={{
                          left: `${interval[0] * generateTimeline().scale}%`,
                          width: `${
                            (interval[1] - interval[0]) *
                            generateTimeline().scale
                          }%`,
                          top: `${idx * 10}px`,
                        }}
                      >
                        <span className="text-xs text-white px-1">
                          [{interval[0]},{interval[1]}]
                        </span>
                      </div>
                    ))}

                    {/* New interval or merged interval */}
                    {currentStepData.mergedInterval ? (
                      <div
                        className="absolute h-8 bg-green-400 border-2 border-green-600 rounded"
                        style={{
                          left: `${
                            currentStepData.mergedInterval[0] *
                            generateTimeline().scale
                          }%`,
                          width: `${
                            (currentStepData.mergedInterval[1] -
                              currentStepData.mergedInterval[0]) *
                            generateTimeline().scale
                          }%`,
                          bottom: "10px",
                        }}
                      >
                        <span className="text-xs text-white px-1">
                          [{currentStepData.mergedInterval[0]},
                          {currentStepData.mergedInterval[1]}]
                        </span>
                      </div>
                    ) : (
                      <div
                        className="absolute h-8 bg-purple-400 border-2 border-purple-600 rounded"
                        style={{
                          left: `${
                            currentStepData.newInterval[0] *
                            generateTimeline().scale
                          }%`,
                          width: `${
                            (currentStepData.newInterval[1] -
                              currentStepData.newInterval[0]) *
                            generateTimeline().scale
                          }%`,
                          bottom: "10px",
                        }}
                      >
                        <span className="text-xs text-white px-1">
                          [{currentStepData.newInterval[0]},
                          {currentStepData.newInterval[1]}]
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Result Array */}
            <div className="mt-4">
              <p className="text-xs font-medium text-gray-700 mb-2">Result:</p>
              <div className="flex flex-wrap gap-1">
                {currentStepData?.result.length > 0 ? (
                  currentStepData.result.map((interval, idx) => (
                    <div
                      key={idx}
                      className="px-2 py-1 bg-green-100 border border-green-300 rounded text-xs font-mono"
                    >
                      [{interval[0]},{interval[1]}]
                    </div>
                  ))
                ) : (
                  <span className="text-xs text-gray-400 italic">Empty</span>
                )}
              </div>
            </div>

            {/* Merge details */}
            {currentStepData?.mergeDetails && (
              <div className="mt-3 p-2 bg-blue-50 rounded">
                <p className="text-xs text-blue-800 font-medium">
                  Merge Operation:
                </p>
                <p className="text-xs text-blue-700">
                  [{currentStepData.mergeDetails.before[0]},
                  {currentStepData.mergeDetails.before[1]}] + [
                  {currentStepData.mergeDetails.merged[0]},
                  {currentStepData.mergeDetails.merged[1]}] = [
                  {currentStepData.mergeDetails.after[0]},
                  {currentStepData.mergeDetails.after[1]}]
                </p>
              </div>
            )}

            {/* Result */}
            {currentStepData?.isFinal && (
              <div className="mt-3 p-3 bg-green-100 rounded">
                <h4 className="text-sm font-bold text-green-900">
                  Operation Complete!
                </h4>
                <p className="text-xs text-green-700 mt-1">
                  Final result:{" "}
                  {currentStepData.result
                    .map((int) => `[${int[0]},${int[1]}]`)
                    .join(", ")}
                </p>
                <p className="text-xs text-green-700">
                  Total intervals: {currentStepData.result.length}
                </p>
              </div>
            )}
          </div>

          {/* Controls */}
          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep === 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>

              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}
                {isPlaying ? "Pause" : "Play"}
              </button>

              <button
                onClick={handleNextStep}
                disabled={currentStep === steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>

              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} />
                Reset
              </button>

              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="200"
                  value={2200 - speed}
                  onChange={(e) => setSpeed(2200 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>

            {/* Step indicator */}
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right side - Pseudo Code & Analysis */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">Algorithm</h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>

          {/* Complexity Analysis */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Complexity Analysis</h3>
            <div className="space-y-2">
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Time Complexity: O(n)
                </h4>
                <p className="text-xs text-gray-600">
                  Single pass through all intervals
                </p>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-700">
                  Space Complexity: O(n)
                </h4>
                <p className="text-xs text-gray-600">
                  Result array to store merged intervals
                </p>
              </div>
            </div>
          </div>

          {/* Algorithm Explanation */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Algorithm Phases</h3>
            <ol className="space-y-2 text-xs text-gray-700">
              <li>
                <span className="font-medium">1. Before Phase:</span>
                <p className="ml-3 text-gray-600">
                  Add all intervals that end before the new interval starts
                </p>
              </li>
              <li>
                <span className="font-medium">2. Merge Phase:</span>
                <p className="ml-3 text-gray-600">
                  Merge all overlapping intervals with the new interval
                </p>
              </li>
              <li>
                <span className="font-medium">3. After Phase:</span>
                <p className="ml-3 text-gray-600">
                  Add all remaining intervals that start after the merged
                  interval
                </p>
              </li>
            </ol>
          </div>

          {/* Key Insights */}
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Intervals are already sorted, enabling single-pass solution
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  Two intervals overlap if: interval1.end ≥ interval2.start
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>Merge by taking min(starts) and max(ends)</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>The algorithm naturally handles all edge cases</span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>No need to sort the result - order is preserved</span>
              </li>
            </ul>
          </div>

          {/* Visual Guide */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-gray-300 border border-gray-400 rounded"></div>
                <span>Original intervals</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-blue-400 border-2 border-blue-600 rounded"></div>
                <span>Current interval being processed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-purple-400 border-2 border-purple-600 rounded"></div>
                <span>New interval to insert</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 h-6 bg-green-400 border-2 border-green-600 rounded"></div>
                <span>Merged interval</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 bg-green-100 border border-green-300 rounded text-xs">
                  [a,b]
                </div>
                <span>Result array intervals</span>
              </div>
            </div>
          </div>

          {/* Merge Conditions */}
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Interval Relationships
            </h3>
            <div className="space-y-2 text-xs">
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">No Overlap (Before):</p>
                <p className="text-gray-600">
                  {"interval.end < newInterval.start"}
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">Overlap:</p>
                <p className="text-gray-600">
                  interval.start ≤ newInterval.end
                </p>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <p className="font-medium">No Overlap (After):</p>
                <p className="text-gray-600">
                  {"interval.start > newInterval.end"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsertIntervalVisualizer;
